// Cruise's camera: the chase rig, the ground stand-off camera it eases on top
// of, and the body pin. All three read the ship's own transform and the
// player's own drag and wheel input; none of it reads the frame time except
// the two eased quantities the player drives.
import * as THREE from 'three';
import {
  gameState, INITIAL_CHASE_DIST, setChaseDist, setChaseDistTarget, setChaseLook, setChaseLookPending,
} from '../state/gameState';
import { addDrag, addShipUpTurn, backToNose, easeLook, lookAngle, rotationVector } from './chaseOrbit';
import type { FlightState } from '../sim/flight';
import type { GravityField } from '../sim/gravityField';
import type { SimPositionHandle } from '../sim/position';
import type { BodyPlaces } from '../sim/bodies';
import type { AtlasWorldHandle } from '../sim/landVerb';
import type { Custos } from '../scene/custos';
import { bodyById } from '../world/bodies';
import type { BodyRow } from '../world/types';
import type { WorldVec } from '../world/frame';
import { urlParam } from '../urlFlags';
import { TEST_SWITCHES } from '../urlFlags.ts';

// ---------- The chase rig ----------
// The cruise camera is a POSE in the ship's own frame, solved fresh from the
// ship's current transform every frame - not a follower lerping toward a
// position the ship has already left. The lerp it replaces used a per-FRAME
// coefficient against a target moving `speed * dt` per frame, so the distance
// it settled at was proportional to the frame time: at 470 m/s a 16.7 ms frame
// left the camera 98 m back and a 35.7 ms frame 115 m back, and every wobble in
// frame time moved it by metres. Nothing here reads the frame time at all
// except the two eased quantities the PLAYER drives (where the look-around has
// been dragged to, and how far the wheel has zoomed), and those ease per wall
// second rather than per frame.
//
// At rest the camera's orientation IS the ship's, so its forward is the nose
// exactly; the ship sits `CHASE_ELEV_RAD` below the middle of the frame because
// the rig is offset up that angle, not because the camera is aimed down. A
// look-around turns the camera and its offset together, so the ship holds the
// same place in frame at any angle and at any zoom. The look-around is a free
// orbit (`chaseOrbit.ts`): a drag turns about the drawn camera's own axes, so it
// goes on round the ship in any direction with no pole to stop at.
const CHASE_ELEV_RAD = 0.12;
/**
 * The nearest the wheel brings the camera, in the drawn hull's own radius
 * (`PlayerShipHandle.hullRadius`), so it is as close to a small hull as to a
 * large one and never inside either. At 1.2 the nearest skin is a fifth of a
 * radius off - 0.8 m for Tower at its 0.45 scale - and the near plane stands
 * off under half of that (`CHASE_NEAR_SHARE`), so the hull cannot clip.
 */
const CHASE_MIN_RADII = 1.2;
/** However the wheel eases, the camera itself never stands closer than this. */
const CHASE_HARD_MIN_RADII = 1.05;
/** A reset stands the camera at the state's opening distance, or this many
 *  radii for a hull large enough that the opening distance would crowd it. */
const CHASE_DEFAULT_RADII = 2.5;
const CHASE_DIST_MAX = 90;
/** The camera's own near plane: the stage's, shortened close in to this share
 *  of the gap to the hull's bounding sphere. The logarithmic depth buffer's
 *  constant is read off the far plane alone (three.js `logDepthBufFC`, which
 *  `src/scene/drivePlume.ts` writes `gl_FragDepth` against), so moving the
 *  near plane moves no depth a plume or the hull draws at. */
const CHASE_NEAR_SHARE = 0.4;
const CHASE_NEAR_FLOOR = 0.01;
/** `?camnear=<m>` pins the near plane, the control
 *  `tools/preview/chase-orbit-check.mjs --inject near-clip` must fail. */
const NEAR_PINNED = TEST_SWITCHES ? Number(urlParam('camnear') ?? NaN) : NaN;
/** `?camreset=0`: the reset asks for nothing, the control of the same check's
 *  reset arm. Nothing a player reaches sets it. */
const RESET_OFF = TEST_SWITCHES && urlParam('camreset') === '0';
/** One wheel notch, multiplicative, so the feel is the same at any distance. */
const CHASE_ZOOM_PER_NOTCH = 1.18;
/** Easing toward the look-around and zoom targets, per WALL second. */
const CHASE_EASE_PER_S = 12;
/** Radians per pixel of left-drag. */
const CHASE_DRAG_RAD_PER_PX = 0.006;
/** Inside this the camera counts as on the nose, and the return control hides. */
const CHASE_ON_NOSE_RAD = 0.005;
/** A look-around turn left under this is made at once rather than eased. */
const CHASE_SNAP_RAD = 1e-5;
/** `?camlook=clamp` (yaw and pitch stopped at 180 and 80 degrees) and `=euler` (no stops)
 *  are the pre-free-orbit look-around, the controls of `tools/preview/chase-orbit-check.mjs`. */
const LOOK_OLD_PARAM = TEST_SWITCHES ? urlParam('camlook') : null;
const LOOK_OLD = LOOK_OLD_PARAM === 'clamp' || LOOK_OLD_PARAM === 'euler' ? LOOK_OLD_PARAM : null;
const LOOK_OLD_PITCH_LIMIT = 1.4;

// ---------- The camera a landing leaves ----------
// Cruise draws a body as one globe map: 2,048 texels round a 3,396 km Mars is
// 10.4 km of ground to a texel, so a camera 18 m behind a resting hull has one
// texel covering six screens and the ground reads as one flat fill. The map
// only reads as ground from a height, so a landing stands the camera off at
// the height that holds one texel to a few pixels, works it out per body from
// that body's own radius and map, and puts the ship's reticle on the spot.

/** Screen pixels one globe-map texel may cover before the map stops reading as ground. */
const GROUND_CAM_TEXEL_PX = 4;
/** Fraction of a body's radius the stand-off is held between, whatever its map says. */
const GROUND_CAM_MIN_RADII = 0.05;
const GROUND_CAM_MAX_RADII = 0.75;
/** Map width assumed for a body whose globe map has not landed yet. */
const GROUND_CAM_MAP_FALLBACK = 2048;
/** Tilt off the local vertical: the resting spot mid frame, the limb and its air above it. */
const GROUND_CAM_TILT_RAD = Math.PI / 4;
/** Wall seconds the stand-off eases in over, and eases back out over on LAUNCH. */
const GROUND_CAM_EASE_S = 1;
/** `?groundcam=0`: a landing left on the chase rig, which is the view before
 *  this existed. A check's own control; nothing a player presses sets it. */
const GROUND_CAM_OFF = TEST_SWITCHES && urlParam('groundcam') === '0';

// ---------- The camera pin ----------
// Off by default. At warp the rig's forward is the nose, which holds its
// attitude while the orbit carries the body it is going round right across the
// screen every revolution. With the pin on the rig's forward is the LINE to
// that body instead, so the body holds the middle of the frame and the ship
// goes round it. Nothing about the ship changes: the attitude, the thrust, the
// assist and the clock are untouched, and the nose marker and the target
// marker go on saying where the bow is pointed.
/** `?pinstale=1`: the pin aims at where the body WAS when it was switched on. */
const PIN_STALE = TEST_SWITCHES && new URLSearchParams(location.search).get('pinstale') === '1';
/** `?pinlag=1`: the pin aims at the place it READ on the previous call, one
 * frame of lag, which is the control the centring bar's 1 px is derived from. */
const PIN_LAG = TEST_SWITCHES && new URLSearchParams(location.search).get('pinlag') === '1';
/** `?pinnoease=1`: a retarget snaps straight to the new row, the pre-ease
 *  behaviour. `tools/preview/pin-swing-check.mjs`'s own control. */
const PIN_NO_EASE = TEST_SWITCHES && new URLSearchParams(location.search).get('pinnoease') === '1';

// ---------- The roll a GO trip holds ----------
// The trip keeps the up it started with, so burn turns bank the ship in frame without rolling the view.
/** Radians a wall second the drawn roll may turn about the forward; a level horizon seen steeply along the held up spins faster and is righted after. */
const HELD_UP_ROLL_RAD_S = 1;
/** Within this of straight along the held up there is no level to turn to. */
const HELD_UP_POLE_RAD = 0.02;
/** `?camrollwithship=1`: the camera rolls with the ship through a GO trip again.
 *  `tools/preview/burn-slew-check.mjs --inject camera-roll`'s own control. */
const CAM_ROLL_WITH_SHIP = TEST_SWITCHES
  && new URLSearchParams(location.search).get('camrollwithship') === '1';

export interface CameraRigLive {
  readonly simBodies: BodyPlaces | null;
  readonly bodyRows: readonly BodyRow[];
}

export interface CameraRigDeps {
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  ship: Custos;
  flight: FlightState;
  simPos: SimPositionHandle;
  gravityField: GravityField;
  live: CameraRigLive;
  atlasWorld(): AtlasWorldHandle | null;
  /** The bubble's inverse, kept current by `syncBubbleFrame()`. */
  invBubble: THREE.Quaternion;
  /** The pin button's own drawn state, which a toggle invalidates. */
  updatePinButton(): void;
  /** True while GO's autopilot has the stick, which is when the rig holds its roll. */
  autopilotFlying(): boolean;
  /** The drawn hull's bounding radius about the ship's origin, scene metres. */
  hullRadius(): number;
}

export interface CameraPinDiagnostics {
  on: boolean;
  bodyId: string | null;
  stale: boolean;
  lag: boolean;
  frames: number;
}

export interface CameraRig {
  /**
   * One cruise frame of the chase rig: drains this frame's look-around drag
   * and wheel notches (a fight drains and drops them instead), solves the
   * camera's pose off the ship's current transform, then eases the ground
   * stand-off camera on top of it.
   */
  update(realDt: number, drag: { dx: number; dy: number }, notches: number): void;
  /** True while the look-around has the camera off the ship's nose. */
  chaseOffNose(): boolean;
  /** Ease the look-around back behind the nose, keeping the zoom. The C key. */
  recentreChase(): void;
  /** Ease the camera back to where a session opens it: behind the nose at the
   *  default distance, with the pin off. The G key and the CHASE button. */
  resetChase(): void;
  /** True while the camera is anywhere but that pose. */
  chaseOffDefault(): boolean;
  /** The nearest and the default chase distance for the hull drawn now, metres. */
  chaseLimits(): { min: number; defaultDist: number; hullRadius: number; max: number };
  /** The camera pin's own switch. Read, set through `setCameraPin`. */
  readonly cameraPinOn: boolean;
  /** The toggle. Recentres both ways, so switching off lands back on the nose. */
  setCameraPin(on: boolean): void;
  /** The row the pin holds: the picked target first, else the frame's own parent. */
  cameraPinBodyId(): string | null;
  /** The pin's switch, the row it is holding, and the controls' flags.
   *  `tools/preview/pin-swing-check.mjs` and its neighbours read this. */
  pinDiagnostics(): CameraPinDiagnostics;
}

export function createCameraRig(deps: CameraRigDeps): CameraRig {
  const { camera, renderer, ship, flight, simPos, gravityField, live, atlasWorld, invBubble } = deps;
  /** The stage's own near plane, which the rig shortens close in and puts back. */
  const baseNear = camera.near;

  // ---------- The chase rig's own scratch ----------
  const _chaseQuat = new THREE.Quaternion();
  const _chaseOffset = new THREE.Vector3();
  const _look = new THREE.Quaternion();
  const _lookWas = new THREE.Quaternion();
  const _pending = new THREE.Vector3();
  const _heldTurn = new THREE.Quaternion();
  const _shipInv = new THREE.Quaternion();
  /** `?camlook=`'s own yaw and pitch, and its scratch. */
  let oldYaw = 0;
  let oldPitch = 0;
  const _oldEuler = new THREE.Euler();
  const _oldTarget = new THREE.Quaternion();

  // ---------- The camera a landing leaves, this rig's own state ----------
  /** 0 on the nose, 1 fully stood off. Eased per wall second, both ways. */
  let groundCamEase = 0;
  const _gcUp = new THREE.Vector3();
  const _gcBack = new THREE.Vector3();
  const _gcDir = new THREE.Vector3();
  const _gcAt = new THREE.Vector3();
  const _gcMat = new THREE.Matrix4();
  const _gcQuat = new THREE.Quaternion();
  const _gcChaseDir = new THREE.Vector3();

  // ---------- The camera pin's own state ----------
  let cameraPinOn = false;
  /** That sample, and the row it was taken for. The control's, nothing else's. */
  const _pinStaleAt: WorldVec = { x: 0, y: 0, z: 0 };
  let pinStaleId: string | null = null;
  /** The previous call's live read, and whether there is one yet. `?pinlag=1`'s. */
  const _pinLagAt: WorldVec = { x: 0, y: 0, z: 0 };
  let pinLagHas = false;
  /** Calls to `cameraPinAim()`, one per frame in cruise. The arm reads this. */
  let pinAimFrames = 0;
  const _pinDir = new THREE.Vector3();
  const _pinNose = new THREE.Vector3();
  const _pinSwing = new THREE.Quaternion();
  /**
   * The aim actually drawn, and the row it was last drawn for - null until the
   * pin has drawn a frame, which is also what `setCameraPin` resets it to on
   * power-on so the first frame of a hold snaps straight to the body.
   *
   * A RETARGET (this row changing while the pin stays on, e.g. the picked
   * target clearing and the aim falling back to the dominant pull) does not
   * move `_pinAimEased` straight to the new row's direction: `_pinErrorQuat`
   * captures the gap between the two and eases shut at the look-around's own
   * rate (`CHASE_EASE_PER_S`, the same `ease` factor below), so the swing
   * catches up to whatever the raw aim is doing rather than chasing a frozen
   * target. Holding the SAME row never touches `_pinErrorQuat` - it stays the
   * identity it starts at, so a hold with no retarget tracks the body exactly
   * as before, one frame at a time, with no lag for the centring reading to see.
   */
  const _pinAimEased = new THREE.Vector3();
  let pinHeldId: string | null = null;
  const _pinErrorQuat = new THREE.Quaternion();
  const _pinIdentityQuat = new THREE.Quaternion();

  // ---------- The held roll's own state ----------
  /** The up the trip holds, in the scene's axes, and whether one is held. */
  const _heldUp = new THREE.Vector3();
  let upHeld = false;
  /** Radians the drawn camera is rolled about its forward off the rig's pose. */
  let heldRoll = 0;
  /** The up the camera was last drawn with, which a new hold starts from. */
  const _drawnUp = new THREE.Vector3(0, 1, 0);
  const _rollFwd = new THREE.Vector3();
  const _rollUp = new THREE.Vector3();
  const _rollWant = new THREE.Vector3();
  const _rollCross = new THREE.Vector3();
  const _rollAxis = new THREE.Vector3(0, 0, 1);
  const _rollQuat = new THREE.Quaternion();

  /**
   * The height the grounded camera stands off at over one body, metres: the
   * height at which one of that body's globe-map texels covers
   * `GROUND_CAM_TEXEL_PX` screen pixels, held inside a band of the body's own
   * radius so a small moon with a large map is not pushed into its own orbit.
   */
  function groundCamHeightM(row: BodyRow): number {
    const mapW = atlasWorld()?.globeMapWidth(row.id) ?? GROUND_CAM_MAP_FALLBACK;
    const texelM = (2 * Math.PI * row.radius_m) / Math.max(mapW, 1);
    const pxRad = ((camera.fov * Math.PI) / 180)
      / Math.max(renderer?.domElement.clientHeight ?? window.innerHeight, 1);
    const want = texelM / (GROUND_CAM_TEXEL_PX * Math.max(pxRad, 1e-12));
    return THREE.MathUtils.clamp(want,
      row.radius_m * GROUND_CAM_MIN_RADII, row.radius_m * GROUND_CAM_MAX_RADII);
  }

  /**
   * Stand the camera off over a resting ship, or bring it back to the nose.
   *
   * Solved on top of the chase rig rather than instead of it, so the player's own
   * yaw still swings the stand-off round the spot and the wheel still zooms - the
   * stand-off is the FLOOR under the zoom while the ship is down, not a lock.
   */
  function applyGroundCamera(realDt: number): void {
    const row = flight.grounded === null || GROUND_CAM_OFF
      ? undefined : bodyById(live.bodyRows, flight.grounded);
    const at = row === undefined ? null : live.simBodies?.worldAt(row.id) ?? null;
    const want = at === null ? 0 : 1;
    groundCamEase += (want - groundCamEase) * (1 - Math.exp(-realDt / GROUND_CAM_EASE_S));
    if (groundCamEase < 1e-3) {
      groundCamEase = 0;
      atlasWorld()?.setShipMarkerVisible(false);
      return;
    }
    atlasWorld()?.setShipMarkerVisible(true);
    if (row === undefined || at === null) return;
    const w = simPos.world;
    _gcUp.set(w.x - at.x, w.y - at.y, w.z - at.z);
    if (_gcUp.lengthSq() === 0) return;
    _gcUp.normalize().applyQuaternion(invBubble);
    // The way the chase rig already faces, flattened onto the ground: the
    // stand-off swings with the player's own look-around instead of picking an
    // axis of its own.
    _gcBack.copy(_chaseOffset).addScaledVector(_gcUp, -_chaseOffset.dot(_gcUp));
    if (_gcBack.lengthSq() < 1e-12) {
      _gcBack.set(0, 0, -1).applyQuaternion(ship.group.quaternion);
      _gcBack.addScaledVector(_gcUp, -_gcBack.dot(_gcUp));
    }
    if (_gcBack.lengthSq() < 1e-12) return;
    _gcBack.normalize();
    const heightM = groundCamHeightM(row);
    _gcDir.copy(_gcUp).addScaledVector(_gcBack, Math.tan(GROUND_CAM_TILT_RAD)).normalize();
    const chaseDist = gameState().ship.chase.dist;
    const standM = Math.max(heightM / Math.cos(GROUND_CAM_TILT_RAD), chaseDist);
    // Geometric between the two, because they are five decades apart and a
    // straight lerp would spend the whole ease already out in orbit.
    const distM = chaseDist * Math.pow(standM / chaseDist, groundCamEase);
    _gcChaseDir.copy(_chaseOffset).normalize();
    _gcDir.lerp(_gcChaseDir, 1 - groundCamEase).normalize();
    camera.position.copy(ship.group.position).addScaledVector(_gcDir, distM);
    _gcAt.copy(ship.group.position);
    _gcMat.lookAt(camera.position, _gcAt, _gcUp);
    _gcQuat.setFromRotationMatrix(_gcMat);
    camera.quaternion.copy(_chaseQuat).slerp(_gcQuat, groundCamEase);
  }

  /** True while the look-around has the camera off the ship's nose. */
  function chaseOffNose(): boolean {
    const chase = gameState().ship.chase;
    const p = chase.lookPending;
    return lookAngle(chase.look) > CHASE_ON_NOSE_RAD || Math.hypot(p.x, p.y, p.z) > CHASE_ON_NOSE_RAD;
  }

  function recentreChase(): void {
    const { look } = gameState().ship.chase;
    setChaseLookPending(backToNose(_pending, _look.set(look.x, look.y, look.z, look.w)));
    oldYaw = 0;
    oldPitch = 0;
  }

  function chaseLimits(): { min: number; defaultDist: number; hullRadius: number; max: number } {
    const r = deps.hullRadius();
    const min = Math.min(CHASE_MIN_RADII * r, CHASE_DIST_MAX);
    return {
      min, hullRadius: r, max: CHASE_DIST_MAX,
      defaultDist: THREE.MathUtils.clamp(Math.max(INITIAL_CHASE_DIST, CHASE_DEFAULT_RADII * r), min, CHASE_DIST_MAX),
    };
  }

  function resetChase(): void {
    if (RESET_OFF) return;
    if (cameraPinOn) setCameraPin(false);
    recentreChase();
    setChaseDistTarget(chaseLimits().defaultDist);
  }

  function chaseOffDefault(): boolean {
    const d = gameState().ship.chase.distTarget;
    return chaseOffNose() || Math.abs(d - chaseLimits().defaultDist) > 0.01 * d;
  }

  /** Bank this frame's drag into `_pending`, about the axes of `_look`. */
  function bankDrag(dx: number, dy: number): void {
    if (LOOK_OLD !== null) {
      oldYaw += dx * CHASE_DRAG_RAD_PER_PX;
      oldPitch += dy * CHASE_DRAG_RAD_PER_PX;
      if (LOOK_OLD === 'clamp') {
        oldYaw = THREE.MathUtils.clamp(oldYaw, -Math.PI, Math.PI);
        oldPitch = THREE.MathUtils.clamp(oldPitch, -LOOK_OLD_PITCH_LIMIT, LOOK_OLD_PITCH_LIMIT);
      }
      _oldTarget.setFromEuler(_oldEuler.set(oldPitch, oldYaw, 0, 'YXZ'));
      rotationVector(_oldTarget.premultiply(_lookWas.copy(_look).invert()), _pending);
    } else if (!GROUND_CAM_OFF && flight.grounded !== null) {
      // Landed, the stand-off keeps its horizon and its fixed tilt: across
      // swings it round the spot about the ship's up, and down is dropped.
      addShipUpTurn(_pending, _look, dx * CHASE_DRAG_RAD_PER_PX);
    } else {
      addDrag(_pending, dx, dy, CHASE_DRAG_RAD_PER_PX, heldRoll);
    }
  }

  /** The row the pin holds: the picked target first, else the frame's own parent. */
  function cameraPinBodyId(): string | null {
    const flightTargetId = gameState().ship.flight.flightTargetId;
    if (flightTargetId !== null && live.simBodies?.worldAt(flightTargetId)) return flightTargetId;
    return gravityField.frameParentId;
  }

  /**
   * Where the pin is aimed this frame, in the bubble's own axes, or null when
   * there is no body to hold.
   *
   * The place is `simBodies.worldAt`, the live read the pilot rows and the target
   * marker already take, so a body the clock is moving is HELD rather than
   * trailed a frame behind. `?pinstale=1` aims at the sample taken when the
   * switch went on instead, and `?pinlag=1` at the previous call's live read,
   * either of which is a control the centring reading fails.
   */
  function cameraPinAim(): THREE.Vector3 | null {
    pinAimFrames++;
    const id = PIN_STALE ? pinStaleId : cameraPinBodyId();
    if (id === null) return null;
    const at = PIN_STALE ? _pinStaleAt : live.simBodies?.worldAt(id) ?? null;
    if (at === null) return null;
    const from = simPos.world;
    // The lag control aims at the previous call's read, which is why the copy of
    // this call's read comes after the aim is taken.
    if (PIN_LAG && pinLagHas) {
      _pinDir.set(_pinLagAt.x - from.x, _pinLagAt.y - from.y, _pinLagAt.z - from.z);
    } else {
      _pinDir.set(at.x - from.x, at.y - from.y, at.z - from.z);
    }
    if (PIN_LAG) {
      _pinLagAt.x = at.x;
      _pinLagAt.y = at.y;
      _pinLagAt.z = at.z;
      pinLagHas = true;
    }
    if (_pinDir.lengthSq() === 0) return null;
    return _pinDir.normalize().applyQuaternion(invBubble);
  }

  /** The toggle. Recentres both ways, so switching off lands back on the nose. */
  function setCameraPin(on: boolean): void {
    if (cameraPinOn === on) return;
    cameraPinOn = on;
    if (on) pinHeldId = null;
    if (on && PIN_STALE) {
      pinStaleId = cameraPinBodyId();
      const at = pinStaleId === null ? null : live.simBodies?.worldAt(pinStaleId) ?? null;
      if (at !== null) { _pinStaleAt.x = at.x; _pinStaleAt.y = at.y; _pinStaleAt.z = at.z; }
    }
    if (on && PIN_LAG) pinLagHas = false;
    recentreChase();
    deps.updatePinButton();
  }

  /** Roll the chase quat about its forward toward the trip's up, or back onto the ship's once the trip lets go, at HELD_UP_ROLL_RAD_S; the forward is untouched. */
  function holdRoll(realDt: number): void {
    let want = 0;
    if (CAM_ROLL_WITH_SHIP || !deps.autopilotFlying()) {
      upHeld = false;
    } else {
      if (!upHeld) {
        _heldUp.copy(_drawnUp);
        upHeld = true;
      }
      _rollFwd.set(0, 0, -1).applyQuaternion(_chaseQuat);
      const along = _rollFwd.dot(_heldUp);
      if (Math.abs(along) > Math.cos(HELD_UP_POLE_RAD)) {
        want = heldRoll;
      } else {
        _rollWant.copy(_heldUp).addScaledVector(_rollFwd, -along).normalize();
        _rollUp.set(0, 1, 0).applyQuaternion(_chaseQuat);
        // Signed about the camera's own +z, which is the axis `_rollQuat` turns on.
        want = Math.atan2(-_rollFwd.dot(_rollCross.crossVectors(_rollUp, _rollWant)),
          _rollUp.dot(_rollWant));
      }
    }
    const off = Math.atan2(Math.sin(want - heldRoll), Math.cos(want - heldRoll));
    const step = HELD_UP_ROLL_RAD_S * realDt;
    heldRoll = Math.abs(off) <= step ? want
      : Math.atan2(Math.sin(heldRoll + Math.sign(off) * step), Math.cos(heldRoll + Math.sign(off) * step));
    if (heldRoll !== 0) _chaseQuat.multiply(_rollQuat.setFromAxisAngle(_rollAxis, heldRoll));
    _drawnUp.set(0, 1, 0).applyQuaternion(_chaseQuat);
  }

  function update(realDt: number, drag: { dx: number; dy: number }, notches: number): void {
    // Cruise: the rig, solved off the ship's pose this frame. The two eased
    // terms are the player's own (look-around and zoom) and ease per wall
    // second, so neither the ship's speed nor the frame time reaches the
    // camera's place behind the nose.
    // With a body pinned the look-around is the pin's, so a drag is drained
    // and dropped rather than banked; the wheel still zooms.
    const pinAim = cameraPinOn ? cameraPinAim() : null;
    const chase = gameState().ship.chase;
    _look.set(chase.look.x, chase.look.y, chase.look.z, chase.look.w);
    _pending.set(chase.lookPending.x, chase.lookPending.y, chase.lookPending.z);
    if (pinAim === null && (drag.dx !== 0 || drag.dy !== 0)) bankDrag(drag.dx, drag.dy);
    const limits = chaseLimits();
    // Clamped every frame, not only on a notch: a swap to a larger hull must
    // push a camera parked at the old hull's nearest back out of the new one.
    const wantDist = THREE.MathUtils.clamp(
      chase.distTarget * Math.pow(CHASE_ZOOM_PER_NOTCH, notches), limits.min, limits.max);
    if (wantDist !== chase.distTarget) setChaseDistTarget(wantDist);
    const ease = 1 - Math.exp(-CHASE_EASE_PER_S * realDt);
    if (_pending.lengthSq() > 0) {
      _lookWas.copy(_look);
      easeLook(_look, _pending, ease, CHASE_SNAP_RAD);
      if (upHeld && pinAim === null) {
        // A GO trip's held up turns with the player's own look-around, so the
        // hold only ever answers the ship's turns and never fights a drag.
        _heldTurn.copy(_lookWas).invert().premultiply(_look).premultiply(ship.group.quaternion)
          .multiply(_shipInv.copy(ship.group.quaternion).invert());
        _heldUp.applyQuaternion(_heldTurn);
      }
      setChaseLook(_look);
      setChaseLookPending(_pending);
    }
    let easedDist = chase.dist + (chase.distTarget - chase.dist) * ease;
    if (Math.abs(easedDist - chase.distTarget) < 1e-4) easedDist = chase.distTarget;
    easedDist = Math.max(easedDist, CHASE_HARD_MIN_RADII * limits.hullRadius);
    setChaseDist(easedDist);
    const near = Number.isFinite(NEAR_PINNED) && NEAR_PINNED > 0 ? NEAR_PINNED
      : THREE.MathUtils.clamp(CHASE_NEAR_SHARE * (easedDist - limits.hullRadius), CHASE_NEAR_FLOOR, baseNear);
    if (near !== camera.near) {
      camera.near = near;
      camera.updateProjectionMatrix();
    }
    // The camera's attitude is the ship's, turned by the look-around: at rest
    // the forward IS the nose, and `camera.up` never enters it, so the roll
    // Q and E put on the hull is carried by the same quaternion. A GO trip's
    // turns are not: `holdRoll` keeps the up the trip started with.
    if (pinAim !== null) {
      // A retarget swings the gap shut rather than chasing the live aim down
      // directly: `_pinErrorQuat` is set once, at the frame the row changes,
      // to the rotation from the NEW raw aim back to the direction actually
      // drawn last frame, then eased toward identity every frame after at the
      // look-around's own rate. A hold with no retarget never sets it away
      // from identity, so `_pinAimEased` tracks `pinAim` exactly, one frame
      // at a time, same as before this row existed.
      const pinId = PIN_STALE ? pinStaleId : cameraPinBodyId();
      if (pinHeldId === null || PIN_NO_EASE) {
        _pinAimEased.copy(pinAim);
        _pinErrorQuat.copy(_pinIdentityQuat);
      } else {
        if (pinId !== pinHeldId) _pinErrorQuat.setFromUnitVectors(pinAim, _pinAimEased);
        else _pinErrorQuat.slerp(_pinIdentityQuat, ease);
        _pinAimEased.copy(pinAim).applyQuaternion(_pinErrorQuat).normalize();
      }
      pinHeldId = pinId;
      // The swing from the nose to the body is the MINIMAL rotation, so the pin
      // adds no roll of its own and Q and E go on rolling the frame.
      _pinNose.set(0, 0, -1).applyQuaternion(ship.group.quaternion);
      _chaseQuat.copy(_pinSwing.setFromUnitVectors(_pinNose, _pinAimEased))
        .multiply(ship.group.quaternion);
    } else {
      _chaseQuat.copy(ship.group.quaternion).multiply(_look);
    }
    holdRoll(realDt);
    camera.quaternion.copy(_chaseQuat);
    // Up the rig's own elevation and back along its own line, so the ship holds
    // one place in frame at every angle and every zoom.
    _chaseOffset.set(0, Math.sin(CHASE_ELEV_RAD), Math.cos(CHASE_ELEV_RAD))
      .multiplyScalar(chase.dist).applyQuaternion(_chaseQuat);
    camera.position.copy(ship.group.position).add(_chaseOffset);
    applyGroundCamera(realDt);
  }

  return {
    update,
    chaseOffNose,
    recentreChase,
    resetChase,
    chaseOffDefault,
    chaseLimits,
    get cameraPinOn(): boolean { return cameraPinOn; },
    setCameraPin,
    cameraPinBodyId,
    pinDiagnostics(): CameraPinDiagnostics {
      return { on: cameraPinOn, bodyId: cameraPinOn ? cameraPinBodyId() : null, stale: PIN_STALE,
        lag: PIN_LAG, frames: pinAimFrames };
    },
  };
}
